import React, { useEffect, useMemo, useState } from 'react';

import { Typography, Grid } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import NotificationsIcon from '@material-ui/icons/Notifications';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import { format, isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { PrintOptions } from 'components/_dialogs/PrintOptionsDialog/PrintOptionsDialog';
import RichTextContent from 'components/RichTextContent/RichTextContent';
import actionInstanceStepApi from 'config/api/actionInstanceStep/actionInstanceStep';
import type { ActionInstanceStepComment, StatusLog } from 'config/api/actionInstanceStep/actionInstanceStep.types';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type {
  ActionLibraryInstance,
  InstanceActivityLogEntry,
  InstanceComment,
} from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import assets_messages from 'messages/assets_messages';
import print_template_messages from 'messages/print_template_messages';
import userDataStorage from 'storages/userDataStorage';

import useStyles from './ActionLibraryInstancePrintTemplate.styles';

/** Inlined so cloned print content has its own CSS (react-to-print does not copy main document styles). */
const PRINT_STYLES = `
.ps-print { padding: 16px; font-size: 14px; }
.ps-print .ps-print__title { margin-bottom: 24px; font-weight: 600; font-size: 1.5rem; }
.ps-print .ps-print__section { margin-bottom: 16px; }
.ps-print .ps-print__label { font-weight: 600; margin-bottom: 4px; display: block; font-size: 0.875rem; }
.ps-print .ps-print__value { margin-bottom: 8px; }
.ps-print .ps-print__value-with-icon { display: inline-flex; align-items: center; vertical-align: middle; }
.ps-print .ps-print__value-with-icon > * + * { margin-left: 4px; }
.ps-print .ps-print__status-icon { margin-right: 4px; font-size: 1.2rem; flex-shrink: 0; }
.ps-print .ps-print__divider { margin-top: 24px; margin-bottom: 24px; border-top: 1px solid #e0e0e0; }
.ps-print .ps-print__section-title { margin-bottom: 16px; font-weight: 600; font-size: 1.25rem; }
.ps-print .ps-print__step-container { margin-bottom: 16px; padding: 16px; border: 1px solid #e0e0e0; border-radius: 4px; page-break-inside: avoid; }
.ps-print .ps-print__step-title { font-weight: 600; margin-bottom: 8px; font-size: 1rem; }
.ps-print .ps-print__step-mandatory { margin-left: 8px; border: 1px solid #e0e0e0; border-radius: 16px; padding: 2px 8px; font-size: 0.75rem; font-weight: normal; display: inline-block; vertical-align: middle; }
.ps-print .ps-print__step-status { margin-left: 8px; font-size: 0.875rem; font-weight: normal; color: #666; }
.ps-print .ps-print__step-content { margin-bottom: 8px; margin-top: 4px; }
.ps-print .ps-print__step-content p { margin: 0 0 4px 0; }
.ps-print .ps-print__step-comments-wrap { margin-top: 12px; border-top: 1px solid #eee; padding-top: 8px; }
.ps-print .ps-print__footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e0e0e0; display: flex; justify-content: space-between; font-size: 0.75rem; color: #666; }
.ps-print .ps-print__comment-item { margin-bottom: 8px; border-bottom: 1px solid #eee; padding-bottom: 8px; }
.ps-print .ps-print__comment-meta { display: block; margin-bottom: 2px; font-size: 0.75rem; color: #666; }
.ps-print .ps-print__comments-list { list-style: none; padding: 0; margin: 0; }
.ps-print .ps-print__comments-list li { margin-bottom: 6px; font-size: 0.75rem; }
.ps-print .ps-print__metadata-row { display: flex; flex-wrap: wrap; gap: 16px 24px; }
.ps-print .ps-print__metadata-cell { flex: 1 1 45%; min-width: 200px; }
.ps-print .ps-print__active-icon { color: #4caf50; }
.ps-print .ps-print__inactive-icon { color: #9e9e9e; }
.ps-print .ps-print__criticality-low { color: #2196f3; }
.ps-print .ps-print__criticality-medium { color: #ff9800; }
.ps-print .ps-print__criticality-high { color: #f44336; }
.ps-print .ps-print__log-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; margin-top: 8px; }
.ps-print .ps-print__log-table th, .ps-print .ps-print__log-table td { border: 1px solid #e0e0e0; padding: 6px 8px; text-align: left; }
.ps-print .ps-print__log-table th { font-weight: 600; background: #f5f5f5; }
.ps-print .ps-print__step-logs-wrap { margin-top: 12px; border-top: 1px solid #eee; padding-top: 8px; }
@media print {
  .ps-print .ps-print__footer { position: fixed; bottom: 0; left: 0; right: 0; padding: 16px; background: #fff; }
}
`;

export type PreFetchedPrintData = {
  instanceComments: InstanceComment[];
  instanceLogs: InstanceActivityLogEntry[];
  stepComments: Record<number, ActionInstanceStepComment[]>;
  stepLogs: Record<number, StatusLog[]>;
};

const instanceStatusKeyMap: Record<string, keyof typeof action_library_messages.table.instance_status> = {
  not_started: 'not_started',
  in_progress: 'in_progress',
  completed: 'completed',
  not_applicable: 'not_applicable',
  blocked: 'blocked',
};

const translateStepStatus = (
  status: string | null | undefined,
  display: string | undefined,
  t: (key: string | string[]) => string,
): string => {
  if (!status) return display || '-';
  const key = instanceStatusKeyMap[status];
  if (key) return t(action_library_messages.table.instance_status[key]);
  return display || status;
};

type Props = {
  instance: ActionLibraryInstance;
  options?: PrintOptions;
  preFetchedPrintData?: PreFetchedPrintData;
};

const ActionLibraryInstancePrintTemplate: React.FC<Props> = ({ instance, options, preFetchedPrintData }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const currentUser = userDataStorage.get();

  const [internalInstanceComments, setInternalInstanceComments] = useState<InstanceComment[]>([]);
  const [internalStepComments, setInternalStepComments] = useState<Record<number, ActionInstanceStepComment[]>>({});

  const instanceComments = preFetchedPrintData?.instanceComments ?? internalInstanceComments;
  const stepComments = preFetchedPrintData?.stepComments ?? internalStepComments;

  // Default to all true if no options provided (backward compatibility)
  const showTitle = options?.title ?? true;
  const showDescription = options?.description ?? true;
  const showMetadata = options?.metadata ?? true;
  const showSpecialInstruction = options?.specialInstruction ?? true;
  const showInstanceComments = options?.instanceComments ?? true;
  const showInstanceLogs = options?.instanceLogs ?? true;
  const showSteps = options?.steps ?? true;
  const showStepComments = options?.stepComments ?? true;
  const showStepActivityLogs = options?.stepActivityLogs ?? true;

  const instanceLogs = preFetchedPrintData?.instanceLogs ?? [];
  const stepLogs = preFetchedPrintData?.stepLogs ?? {};

  useEffect(() => {
    if (preFetchedPrintData != null || (!showInstanceComments && !showStepComments)) return;

    const fetchComments = async () => {
      try {
        if (showInstanceComments) {
          const { data: instanceCommentsData } = await actionLibraryInstancesApi.getInstanceComments(instance.id);
          setInternalInstanceComments(instanceCommentsData.results);
        }

        if (showStepComments && instance.steps) {
          const stepCommentsPromises = instance.steps.map(step =>
            actionInstanceStepApi.getComments(step.id).then(res => ({ stepId: step.id, comments: res.data.results })),
          );
          const stepCommentsResults = await Promise.all(stepCommentsPromises);
          const stepCommentsMap = stepCommentsResults.reduce((acc, { stepId, comments }) => {
            acc[stepId] = comments;
            return acc;
          }, {} as Record<number, ActionInstanceStepComment[]>);
          setInternalStepComments(stepCommentsMap);
        }
      } catch {
        // Fallback: render without comments when fetch fails (e.g. when used without pre-fetch)
      }
    };

    fetchComments();
  }, [instance.id, instance.steps, showInstanceComments, showStepComments, preFetchedPrintData]);

  const deadlineDate = instance.deadline ? new Date(instance.deadline) : null;
  const reminderDate = instance.reminder ? new Date(instance.reminder) : null;
  const isDeadlinePast = deadlineDate && isPast(deadlineDate) && instance.status !== 'completed';
  const isReminderPast = reminderDate && isPast(reminderDate) && instance.status !== 'completed';

  const statusData = useMemo(() => {
    let StatusIconComponent: React.ComponentType<{ className?: string }>;
    let statusIconClass: string;
    let printIconClass: string;
    if (instance.status === 'completed') {
      StatusIconComponent = CheckCircleIcon;
      statusIconClass = styles.activeIcon;
      printIconClass = 'ps-print__active-icon';
    } else if (instance.status === 'in_progress') {
      StatusIconComponent = RemoveCircleOutlineIcon;
      statusIconClass = styles.criticalityMediumIcon;
      printIconClass = 'ps-print__criticality-medium';
    } else {
      StatusIconComponent = CancelIcon;
      statusIconClass = styles.inactiveIcon;
      printIconClass = 'ps-print__inactive-icon';
    }

    return {
      StatusIconComponent,
      statusIconClass,
      printIconClass,
      displayValue: instance.status,
    };
  }, [instance.status, styles]);

  const criticalityData = useMemo(() => {
    const criticalityKey = instance.criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';
    let CriticalityIconComponent: React.ComponentType<{ className?: string }>;
    let criticalityIconClass: string;
    let printIconClass: string;

    if (instance.criticality === 'low') {
      CriticalityIconComponent = InfoIcon;
      criticalityIconClass = styles.criticalityLowIcon;
      printIconClass = 'ps-print__criticality-low';
    } else if (instance.criticality === 'medium') {
      CriticalityIconComponent = WarningIcon;
      criticalityIconClass = styles.criticalityMediumIcon;
      printIconClass = 'ps-print__criticality-medium';
    } else {
      CriticalityIconComponent = ErrorIcon;
      criticalityIconClass = styles.criticalityHighIcon;
      printIconClass = 'ps-print__criticality-high';
    }

    return {
      CriticalityIconComponent,
      criticalityIconClass,
      printIconClass,
      displayValue: t(action_library_messages.criticality[criticalityKey]),
    };
  }, [instance.criticality, t, styles]);

  const StatusIcon = statusData.StatusIconComponent;
  const CriticalityIcon = criticalityData.CriticalityIconComponent;

  return (
    <>
      {/* Static CSS constant (no user input) – required so cloned print content has its own styles */}
      {/* eslint-disable-next-line react/no-danger */}
      <style dangerouslySetInnerHTML={{ __html: PRINT_STYLES }} />
      <div className={`${styles.root} ps-print`}>
        {showTitle && (
          <Typography className={`${styles.title} ps-print__title`} variant='h3'>
            {instance.title}
          </Typography>
        )}

        {showDescription && (
          <div className={`${styles.section} ps-print__section`}>
            <div className={`${styles.value} ps-print__value`}>
              {instance.description ? <RichTextContent content={instance.description} /> : '-'}
            </div>
          </div>
        )}

        {showMetadata && (
          <Grid className={`ps-print__metadata-row ${styles.section}`} container spacing={2}>
            {/* Row 1: Status | Criticality */}
            <Grid className='ps-print__metadata-cell' item xs={6}>
              <div className={styles.section}>
                <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                  {t(action_library_messages.instance.detail.status)}
                </Typography>
                <Typography className={styles.value} variant='body1'>
                  <div className={`${styles.valueWithIcon} ps-print__value-with-icon`}>
                    <StatusIcon
                      className={`${styles.statusIcon} ps-print__status-icon ${statusData.statusIconClass} ${statusData.printIconClass}`}
                    />
                    <span>
                      {instance.statusDisplay || statusData.displayValue}
                      {typeof instance.statusPercent === 'number' && ` - ${instance.statusPercent}%`}
                    </span>
                  </div>
                </Typography>
              </div>
            </Grid>
            <Grid className='ps-print__metadata-cell' item xs={6}>
              <div className={styles.section}>
                <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                  {t(action_library_messages.instance.detail.criticality)}
                </Typography>
                <Typography className={styles.value} variant='body1'>
                  <div className={`${styles.valueWithIcon} ps-print__value-with-icon`}>
                    <CriticalityIcon
                      className={`${styles.statusIcon} ps-print__status-icon ${criticalityData.criticalityIconClass} ${criticalityData.printIconClass}`}
                    />
                    <span>{criticalityData.displayValue}</span>
                  </div>
                </Typography>
              </div>
            </Grid>
            {/* Row 2: Template Name | Template Source */}
            <Grid className='ps-print__metadata-cell' item xs={6}>
              <div className={styles.section}>
                <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                  {t(action_library_messages.instance.detail.template_name)}
                </Typography>
                <Typography className={`${styles.value} ps-print__value`} variant='body1'>
                  {instance.templateName ?? '–'}
                </Typography>
              </div>
            </Grid>
            <Grid className='ps-print__metadata-cell' item xs={6}>
              <div className={styles.section}>
                <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                  {t(action_library_messages.instance.detail.template_source)}
                </Typography>
                <Typography className={`${styles.value} ps-print__value`} variant='body1'>
                  {instance.templateSourceDisplay ?? instance.templateSource ?? '–'}
                </Typography>
              </div>
            </Grid>
            {/* Row 3: Asset Name | Asset Type */}
            <Grid className='ps-print__metadata-cell' item xs={6}>
              <div className={styles.section}>
                <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                  {t(action_library_messages.instance.detail.asset_name)}
                </Typography>
                <Typography className={`${styles.value} ps-print__value`} variant='body1'>
                  {instance.assetName || '–'}
                </Typography>
              </div>
            </Grid>
            <Grid className='ps-print__metadata-cell' item xs={6}>
              <div className={styles.section}>
                <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                  {t(action_library_messages.instance.detail.asset_type)}
                </Typography>
                <Typography className={`${styles.value} ps-print__value`} variant='body1'>
                  {instance.assetType || '–'}
                </Typography>
              </div>
            </Grid>
            {/* Row 4: Asset Importance | Owner */}
            <Grid className='ps-print__metadata-cell' item xs={6}>
              <div className={styles.section}>
                <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                  {t(action_library_messages.instance.detail.asset_importance)}
                </Typography>
                <Typography className={`${styles.value} ps-print__value`} variant='body1'>
                  {instance.assetImportance
                    ? // @ts-ignore
                      t(assets_messages.information_choices_assets_importance[instance.assetImportance]) || instance.assetImportance
                    : '–'}
                </Typography>
              </div>
            </Grid>
            <Grid className='ps-print__metadata-cell' item xs={6}>
              <div className={styles.section}>
                <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                  {t(action_library_messages.instance.detail.owner)}
                </Typography>
                <Typography className={`${styles.value} ps-print__value`} variant='body1'>
                  {instance.ownerFullName || '–'}
                </Typography>
              </div>
            </Grid>
            {/* Row 5: Reported To | Due date */}
            <Grid className='ps-print__metadata-cell' item xs={6}>
              <div className={styles.section}>
                <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                  {t(action_library_messages.instance.detail.reported_to)}
                </Typography>
                <Typography className={`${styles.value} ps-print__value`} variant='body1'>
                  {instance.reportedTo ?? '–'}
                </Typography>
              </div>
            </Grid>
            <Grid className='ps-print__metadata-cell' item xs={6}>
              <div className={styles.section}>
                <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                  {t(action_library_messages.instance.detail.deadline)}
                </Typography>
                <Typography
                  className={styles.value}
                  style={{ color: isDeadlinePast ? '#f44336' : 'inherit', fontWeight: isDeadlinePast ? 'bold' : 'normal' }}
                  variant='body1'
                >
                  {deadlineDate ? format(deadlineDate, 'yyyy-MM-dd HH:mm') : '–'}
                </Typography>
              </div>
            </Grid>
            {/* Row 6: Reminder | Validity Period */}
            <Grid className='ps-print__metadata-cell' item xs={6}>
              <div className={styles.section}>
                <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                  {t(action_library_messages.instance.detail.reminder)}
                </Typography>
                <Typography className={styles.value} style={{ color: isReminderPast ? '#ff9800' : 'inherit' }} variant='body1'>
                  {reminderDate ? (
                    <div className={`${styles.valueWithIcon} ps-print__value-with-icon`}>
                      <NotificationsIcon style={{ fontSize: '1rem', color: isReminderPast ? '#ff9800' : 'rgba(0, 0, 0, 0.54)' }} />
                      <span>{format(reminderDate, 'yyyy-MM-dd HH:mm')}</span>
                    </div>
                  ) : (
                    '–'
                  )}
                </Typography>
              </div>
            </Grid>
            <Grid className='ps-print__metadata-cell' item xs={6}>
              <div className={styles.section}>
                <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                  {t(action_library_messages.instance.detail.validation_period)}
                </Typography>
                <Typography className={`${styles.value} ps-print__value`} variant='body1'>
                  {instance.validityPeriodMonths != null
                    ? t(action_library_messages.instance.detail.validation_period_months, { count: instance.validityPeriodMonths })
                    : '–'}
                </Typography>
              </div>
            </Grid>
          </Grid>
        )}

        {showSpecialInstruction && instance.specialInstruction && (
          <>
            <div className={`${styles.divider} ps-print__divider`} />
            <div className={`${styles.section} ps-print__section`}>
              <Typography className={`${styles.label} ps-print__label`} variant='body2'>
                {t(action_library_messages.instance.detail.special_instruction)}
              </Typography>
              <Typography className={`${styles.value} ps-print__value`} component='div' variant='body1'>
                <RichTextContent content={instance.specialInstruction} />
              </Typography>
            </div>
          </>
        )}

        {showInstanceComments && (instance.completionComment || instanceComments.length > 0) && (
          <>
            <div className={`${styles.divider} ps-print__divider`} />
            <div className={`${styles.section} ps-print__section`}>
              <Typography className={`${styles.sectionTitle} ps-print__section-title`} variant='h5'>
                {t(action_library_messages.instance_actions.instance_comments)}
              </Typography>

              {instance.completionComment && (
                <div className='ps-print__comment-item' style={{ marginBottom: 16 }}>
                  <Typography style={{ fontWeight: 'bold' }} variant='subtitle2'>
                    {t(action_library_messages.instance.detail.completion_comment)}:
                  </Typography>
                  <Typography variant='body2'>{instance.completionComment}</Typography>
                </div>
              )}

              {instanceComments.length > 0 && (
                <ul className='ps-print__comments-list'>
                  {instanceComments.map(comment => {
                    let purposeLabel: string | null = null;
                    if (comment.purpose === 2) {
                      purposeLabel = t(action_library_messages.instance_actions.comment_purpose_delegation);
                    } else if (comment.purpose === 3) {
                      purposeLabel = t(action_library_messages.instance_actions.comment_purpose_completion);
                    }
                    return (
                      <li key={comment.id} className='ps-print__comment-item'>
                        <span className='ps-print__comment-meta'>
                          {comment.userFullName || comment.userEmail} - {format(new Date(comment.createdAt), 'yyyy-MM-dd HH:mm')}
                          {purposeLabel != null && ` · ${purposeLabel}`}
                        </span>
                        <Typography variant='body2'>{comment.text}</Typography>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}

        {showInstanceLogs && instanceLogs.length > 0 && (
          <>
            <div className={`${styles.divider} ps-print__divider`} />
            <div className={`${styles.section} ps-print__section`}>
              <Typography className={`${styles.sectionTitle} ps-print__section-title`} variant='h5'>
                {t(action_library_messages.instance.detail.action_log)}
              </Typography>
              <table className='ps-print__log-table'>
                <thead>
                  <tr>
                    <th>{t(action_library_messages.instance.detail.action_log_date)}</th>
                    <th>{t(action_library_messages.instance.detail.action_log_change)}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...instanceLogs]
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map(log => (
                      <tr key={`${log.id}-${log.createdAt}`}>
                        <td>{format(new Date(log.createdAt), 'yyyy-MM-dd HH:mm')}</td>
                        <td>{log.message ?? '—'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {showSteps && instance.steps && instance.steps.length > 0 && (
          <>
            <div className={`${styles.divider} ps-print__divider`} />
            <div className={`${styles.section} ps-print__section`}>
              <Typography className={`${styles.sectionTitle} ps-print__section-title`} variant='h5'>
                {t(action_library_messages.instance.detail.steps)}
              </Typography>
              {[...instance.steps]
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((step, index) => (
                  <div key={step.id} className={`${styles.stepContainer} ps-print__step-container`}>
                    <Typography className={`${styles.stepTitle} ps-print__step-title`} component='div' variant='subtitle1'>
                      <span>
                        {index + 1}. {step.title}
                      </span>
                      {step.mandatory && (
                        <span className={`${styles.mandatoryBadge} ps-print__step-mandatory`}>
                          {' '}
                          {t(action_library_messages.instance.form.mandatory)}
                        </span>
                      )}
                      <span className={`${styles.stepStatus} ps-print__step-status`}> {step.statusDisplay || step.status || '-'}</span>
                    </Typography>
                    <div className={`${styles.stepContent} ps-print__step-content`}>
                      <RichTextContent content={step.instruction} />
                    </div>
                    {(step.reference || step.relevantFor) && (
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                        {step.reference && (
                          <span>
                            <strong>{t(action_library_messages.steps.related_reading)}:</strong> {step.reference}
                          </span>
                        )}
                        {step.relevantFor && (
                          <span>
                            <strong>{t(action_library_messages.steps.relevant_for)}:</strong> {step.relevantFor}
                          </span>
                        )}
                      </div>
                    )}
                    {step.tags && typeof step.tags === 'object' && Object.keys(step.tags).length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Typography style={{ fontWeight: 'bold', marginBottom: 4 }} variant='caption'>
                          {t(action_library_messages.detail.tags)}:
                        </Typography>
                        <span style={{ marginRight: 8 }}>
                          {Object.entries(step.tags)
                            .map(([, value]) => String(value).toLowerCase())
                            .join(', ')}
                        </span>
                      </div>
                    )}
                    {showStepComments && stepComments[step.id] && stepComments[step.id].length > 0 && (
                      <div className='ps-print__step-comments-wrap'>
                        <Typography style={{ fontWeight: 'bold' }} variant='caption'>
                          {t(action_library_messages.instance_actions.step_comments)}:
                        </Typography>
                        <ul className='ps-print__comments-list'>
                          {stepComments[step.id].map(comment => (
                            <li key={comment.id}>
                              <span style={{ fontWeight: 600 }}>
                                {comment.userFullName || comment.userEmail} ({format(new Date(comment.createdAt), 'yyyy-MM-dd HH:mm')}):{' '}
                              </span>
                              {comment.text}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {showStepActivityLogs && stepLogs[step.id] && stepLogs[step.id].length > 0 && (
                      <div className='ps-print__step-logs-wrap'>
                        <Typography style={{ fontWeight: 'bold' }} variant='caption'>
                          {t(action_library_messages.instance.detail.action_log)}:
                        </Typography>
                        <table className='ps-print__log-table'>
                          <thead>
                            <tr>
                              <th>{t(action_library_messages.instance.detail.action_log_date)}</th>
                              <th>{t(action_library_messages.instance.detail.action_log_user)}</th>
                              <th>{t(action_library_messages.instance.detail.action_log_change)}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...stepLogs[step.id]]
                              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                              .map(log => (
                                <tr key={`${log.id}-${log.createdAt}`}>
                                  <td>{format(new Date(log.createdAt), 'yyyy-MM-dd HH:mm')}</td>
                                  <td>{log.userFullName || log.userEmail || '–'}</td>
                                  <td>
                                    {translateStepStatus(log.oldStatus, log.oldStatusDisplay, t)} →{' '}
                                    {translateStepStatus(log.newStatus, log.newStatusDisplay, t)}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </>
        )}

        <div className={`${styles.footer} ps-print__footer`}>
          <div>
            {t(print_template_messages.footer.printed_by)}: {currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : '-'}
          </div>
          <div>
            {t(print_template_messages.footer.printed_on)}: {format(new Date(), 'yyyy-MM-dd HH:mm')}
          </div>
        </div>
      </div>
    </>
  );
};

export default ActionLibraryInstancePrintTemplate;
